import { Link } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";

const num = (n) => (Math.round((n || 0) * 100) / 100).toLocaleString();
const fmtD = (d) => (d ? new Date(`${d.slice(0, 10)}T00:00:00`).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "—");

// Every shipped sales order for this finished-good row, plus the running ledger math.
export function SalesHistoryDialog({ histKey, name, history, onClose }) {
  const entry = histKey ? history[histKey] : null;
  const orders = entry?.orders || [];
  const adj = entry?.adjusted || 0;
  return (
    <Dialog open={!!histKey} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-h-[85vh] overflow-y-auto rounded-sm sm:max-w-2xl" data-testid="sales-history-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading tracking-tight">Sales History</DialogTitle>
          <DialogDescription>{name} — every shipped order for this item.</DialogDescription>
        </DialogHeader>
        {orders.length === 0 ? (
          <p className="py-6 text-center text-sm text-slate-400">Nothing shipped for this item yet.</p>
        ) : (
          <table className="w-full border-collapse text-sm" data-testid="sales-history-table">
            <thead>
              <tr className="text-[10px] uppercase tracking-[0.08em] text-slate-500">
                <th className="border-b border-slate-200 px-2 py-2 text-left font-semibold">Date</th>
                <th className="border-b border-slate-200 px-2 py-2 text-left font-semibold">SO</th>
                <th className="border-b border-slate-200 px-2 py-2 text-left font-semibold">BOL</th>
                <th className="border-b border-slate-200 px-2 py-2 text-left font-semibold">Customer</th>
                <th className="border-b border-slate-200 px-2 py-2 text-right font-semibold">Cases</th>
              </tr>
            </thead>
            <tbody>
              {orders.map((o, i) => (
                <tr key={i} className="text-slate-700" data-testid={`sales-history-row-${i}`}>
                  <td className="border-b border-slate-100 px-2 py-2 whitespace-nowrap">{fmtD(o.date)}</td>
                  <td className="border-b border-slate-100 px-2 py-2 font-semibold whitespace-nowrap">
                    {o.so_number ? <Link to="/sales-orders" className="text-[#002FA7] hover:underline">{o.so_number}</Link> : "—"}
                  </td>
                  <td className="border-b border-slate-100 px-2 py-2 whitespace-nowrap">
                    {o.do_number || <span className="text-[10px] font-medium uppercase text-slate-400" title="Marked shipped before BOL tracking — no stock movement recorded">marked shipped</span>}
                  </td>
                  <td className="border-b border-slate-100 px-2 py-2">{o.customer_name || "—"}</td>
                  <td className="border-b border-slate-100 px-2 py-2 text-right font-medium tabular-nums">{num(o.cases)}</td>
                </tr>
              ))}
              <tr className="text-slate-900">
                <td className="px-2 py-2 text-xs font-semibold uppercase tracking-wide" colSpan={4}>Total shipped</td>
                <td className="px-2 py-2 text-right font-bold tabular-nums" data-testid="sales-history-total">{num(entry?.shipped || 0)}</td>
              </tr>
            </tbody>
          </table>
        )}
        {entry && (entry.produced > 0 || entry.shipped_ledger > 0 || adj !== 0) && (
          <p className="rounded-sm border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600" data-testid="sales-history-recon">
            <span className="font-semibold uppercase tracking-wide text-slate-500">Running math:</span>{" "}
            Produced <span className="font-semibold tabular-nums">{num(entry.produced)}</span>
            {" − "}Shipped <span className="font-semibold tabular-nums">{num(entry.shipped_ledger)}</span>
            {adj !== 0 && <>{adj > 0 ? " + " : " − "}Adjustments <span className="font-semibold tabular-nums">{num(Math.abs(adj))}</span></>}
            {" = "}<span className="font-bold tabular-nums">{num(entry.on_hand)}</span> on hand
          </p>
        )}
        <p className="text-right text-xs">
          <Link to="/sales-orders" className="font-semibold text-[#002FA7] hover:underline" data-testid="sales-history-open-page">Open Sales Orders →</Link>
        </p>
      </DialogContent>
    </Dialog>
  );
}
